import type { CSSProperties } from "react";

export interface ModuleAccent {
  id: string;
  label: string;
  /** Primary accent color (hex) */
  color: string;
  /** Light background tint (hex) */
  tint: string;
  /** Border color variant (hex) */
  border: string;
  /** Foreground text on tint background (hex) */
  foreground: string;
  /** Lucide icon name */
  icon: string;
  /** Tailwind text class for the accent */
  textClass: string;
  /** Tailwind bg class for the tint */
  bgClass: string;
  /**
   * Icon color for a SOLID `color`-background tile (icon badges in hero
   * pulse-stat strips, DecisionRail quick-actions, etc — the mockup's bold
   * treatment, not the pale-tint one `foreground` is for). Defaults to white;
   * only set when `color` is too light for white to clear 3:1 contrast (only
   * "home"'s amber needs this — reuses the same dark-ink-on-amber value the
   * button/badge rule already established at `--primary-tint-foreground`).
   */
  solidTileForeground?: string;
  /**
   * Dark-mode values (DARK_MODE.md § Module accents). `color` is for icons, underlines, rails and
   * bars, and is also the text color on dark; every one clears 4.5:1 on the `#1f1d1a` card surface
   * (pinned by module-accents.test.ts). `tint` is a translucent wash for chips and active nav, so
   * it is an rgba() string rather than a hex.
   */
  dark: { color: string; tint: string };
}

const moduleAccents: ModuleAccent[] = [
  // ── Hub / Dashboard ──────────────────────────────
  {
    id: "home",
    label: "Home",
    color: "#d49b0a",
    tint: "#fdf6e4",
    border: "#ecd8a0",
    foreground: "#9a7008",
    icon: "House",
    textClass: "text-[#d49b0a]",
    bgClass: "bg-[#fdf6e4]",
    dark: { color: "#e6b84e", tint: "rgba(230,184,78,.13)" },
    // #d49b0a only clears ~2.5:1 against white — below the 3:1 UI-graphic
    // minimum — so a solid tile needs the dark-ink treatment instead: the ink on gold
    // (--primary-foreground, #1c1606, about 7:1 in both themes), not the ink on the tint.
    solidTileForeground: "hsl(var(--primary-foreground))",
  },
  // ── Calendar / Scheduling ────────────────────────
  {
    id: "calendar",
    label: "Calendar",
    color: "#0e7c7b",
    tint: "#e6f5f5",
    border: "#a8d8d8",
    foreground: "#0a5c5c",
    icon: "CalendarBlank",
    textClass: "text-[#0e7c7b]",
    bgClass: "bg-[#e6f5f5]",
    dark: { color: "#4cc2bf", tint: "rgba(76,194,191,.13)" },
  },
  // ── People / Directory ───────────────────────────
  {
    id: "people",
    label: "People",
    color: "#2563a8",
    tint: "#eaf2fb",
    border: "#b8d4f0",
    foreground: "#1a4a80",
    icon: "Users",
    textClass: "text-[#2563a8]",
    bgClass: "bg-[#eaf2fb]",
    dark: { color: "#78acdf", tint: "rgba(120,172,223,.14)" },
  },
  // ── HR ───────────────────────────────────────────
  {
    id: "hr",
    label: "HR",
    color: "#b04060",
    tint: "#fceef2",
    border: "#eab8c8",
    foreground: "#8a2e4a",
    icon: "BadgeCheck",
    textClass: "text-[#b04060]",
    bgClass: "bg-[#fceef2]",
    dark: { color: "#e58fab", tint: "rgba(229,143,171,.13)" },
  },
  // ── Finance ──────────────────────────────────────
  {
    id: "finance",
    label: "Finance",
    color: "#1a7a4c",
    tint: "#e8f5ee",
    border: "#b8dcc8",
    foreground: "#145c39",
    icon: "Landmark",
    textClass: "text-[#1a7a4c]",
    bgClass: "bg-[#e8f5ee]",
    dark: { color: "#5ccb8e", tint: "rgba(92,203,142,.13)" },
  },
  // ── Schools ─────────────────────────────────────
  {
    id: "schools",
    label: "Schools",
    color: "#0f766e",
    tint: "#e6f6f3",
    border: "#a7d8d0",
    foreground: "#0b5c55",
    icon: "GraduationCap",
    textClass: "text-[#0f766e]",
    bgClass: "bg-[#e6f6f3]",
    dark: { color: "#4cc4b4", tint: "rgba(76,196,180,.13)" },
  },
  // Legacy Kids Check-In / childcare accent alias.
  {
    id: "childcare",
    label: "Kids Check-In",
    color: "#0f766e",
    tint: "#e6f6f3",
    border: "#a7d8d0",
    foreground: "#0b5c55",
    icon: "Baby",
    textClass: "text-[#0f766e]",
    bgClass: "bg-[#e6f6f3]",
    dark: { color: "#4cc4b4", tint: "rgba(76,196,180,.13)" },
  },
  // ── Ministry ─────────────────────────────────────
  {
    id: "ministry",
    label: "Ministry",
    color: "#6d3fa0",
    tint: "#f0eaf8",
    border: "#c8b0e0",
    foreground: "#532e80",
    icon: "Church",
    textClass: "text-[#6d3fa0]",
    bgClass: "bg-[#f0eaf8]",
    dark: { color: "#b893dc", tint: "rgba(184,147,220,.14)" },
  },
  // ── Operations / Projects ────────────────────────
  {
    id: "projects",
    label: "Projects",
    color: "#c06030",
    tint: "#fdf0e8",
    border: "#e0c0a8",
    foreground: "#944820",
    icon: "FolderKanban",
    textClass: "text-[#c06030]",
    bgClass: "bg-[#fdf0e8]",
    dark: { color: "#eb9866", tint: "rgba(235,152,102,.13)" },
  },
  // ── Messaging / Communications ───────────────────
  {
    id: "messaging",
    label: "Messaging",
    color: "#0891b2",
    tint: "#e6f6fa",
    border: "#a0d8e8",
    foreground: "#066d88",
    icon: "PaperPlaneTilt",
    textClass: "text-[#0891b2]",
    bgClass: "bg-[#e6f6fa]",
    dark: { color: "#4fc8e4", tint: "rgba(79,200,228,.13)" },
  },
  // ── Reports / Analytics ──────────────────────────
  {
    id: "reports",
    label: "Reports",
    color: "#d0505a",
    tint: "#fdeced",
    border: "#e8b0b4",
    foreground: "#a03840",
    icon: "ChartBar",
    textClass: "text-[#d0505a]",
    bgClass: "bg-[#fdeced]",
    dark: { color: "#f08b92", tint: "rgba(240,139,146,.13)" },
  },
  // ── Member Portal ────────────────────────────────
  {
    id: "memberPortal",
    label: "Member Portal",
    color: "#8a6518",
    tint: "#f8f2e4",
    border: "#d8c898",
    foreground: "#6a4e10",
    icon: "User",
    textClass: "text-[#8a6518]",
    bgClass: "bg-[#f8f2e4]",
    dark: { color: "#d6ab52", tint: "rgba(214,171,82,.13)" },
  },
  // ── Attendance (sub-module, used by check-ins) ───
  {
    id: "attendance",
    label: "Attendance",
    color: "#0e7c7b",
    tint: "#e6f5f5",
    border: "#a8d8d8",
    foreground: "#0a5c5c",
    icon: "CalendarCheck",
    textClass: "text-[#0e7c7b]",
    bgClass: "bg-[#e6f5f5]",
    dark: { color: "#4cc2bf", tint: "rgba(76,194,191,.13)" },
  },
  // ── Volunteer ────────────────────────────────────
  {
    id: "volunteer",
    label: "Volunteer",
    color: "#b87a1a",
    tint: "#fdf4e4",
    border: "#e8d4a8",
    foreground: "#8a5c10",
    icon: "HandHeart",
    textClass: "text-[#b87a1a]",
    bgClass: "bg-[#fdf4e4]",
    dark: { color: "#e0a24a", tint: "rgba(224,162,74,.13)" },
  },
  // ── Communications (legacy alias for messaging) ──
  {
    id: "communications",
    label: "Communications",
    color: "#0891b2",
    tint: "#e6f6fa",
    border: "#a0d8e8",
    foreground: "#066d88",
    icon: "PaperPlaneTilt",
    textClass: "text-[#0891b2]",
    bgClass: "bg-[#e6f6fa]",
    dark: { color: "#4fc8e4", tint: "rgba(79,200,228,.13)" },
  },
  // ── Settings ─────────────────────────────────────
  {
    id: "settings",
    label: "Settings",
    color: "#5c6878",
    tint: "#f0f1f3",
    border: "#c8ccd2",
    foreground: "#444e58",
    icon: "Settings",
    textClass: "text-[#5c6878]",
    bgClass: "bg-[#f0f1f3]",
    dark: { color: "#a8b0ba", tint: "rgba(168,176,186,.12)" },
  },
];

export const moduleAccentMap: Record<string, ModuleAccent> = moduleAccents.reduce(
  (acc, accent) => {
    acc[accent.id] = accent;
    return acc;
  },
  {} as Record<string, ModuleAccent>
);

const settingsAccent = moduleAccentMap["settings"];

export function getModuleAccent(moduleId: string): ModuleAccent {
  return moduleAccentMap[moduleId] || settingsAccent;
}

/**
 * Solid, saturated icon-tile style — the bold treatment for standalone icon
 * badges (hero pulse stats, DecisionRail quick-actions, KPI card corner
 * badges): full `color` background with a contrast-safe icon color, instead
 * of `getModuleAccent`'s pale `tint` background + `color` icon pairing used
 * for inline/nav contexts where a loud solid badge would be too heavy.
 */
export function getModuleAccentTileStyle(moduleId: string): { backgroundColor: string; color: string } {
  const accent = getModuleAccent(moduleId);
  return { backgroundColor: accent.color, color: accent.solidTileForeground ?? "#ffffff" };
}

/** The props `moduleAccentScope` returns: spread them onto the element that scopes a module. */
export interface ModuleAccentScope {
  "data-module-scope": string;
  style: CSSProperties;
}

/**
 * A module accent scope (#7915): what AppLayout spreads onto its root, and what a part that shows
 * one module's accent wherever it sits (a module sub-nav host) spreads onto its own wrapper. It
 * hands over the module's light and dark values; `[data-module-scope]` in src/index.css turns them
 * into the `--module-color`, `--module-tint`, `--module-border` and `--module-foreground` every
 * consumer reads (Tailwind's `module` colours among them): the light values, and under the next
 * chassis in dark mode the dark ones (DARK_MODE.md § Module accents). Classic keeps the light
 * values it always had, in both themes.
 */
export function moduleAccentScope(accent: ModuleAccent): ModuleAccentScope {
  return {
    "data-module-scope": accent.id,
    style: {
      "--module-color-light": accent.color,
      "--module-tint-light": accent.tint,
      "--module-border-light": accent.border,
      "--module-foreground-light": accent.foreground,
      "--module-color-dark": accent.dark.color,
      "--module-tint-dark": accent.dark.tint,
    } as CSSProperties,
  };
}

export function getModuleForPath(pathname: string): ModuleAccent {
  const path = pathname.toLowerCase();

  // Home / Dashboard
  if (path === "/" || path === "/today" || path === "/dashboard") {
    return getModuleAccent("home");
  }

  // Member Portal — checked early, before any of the module-specific
  // branches below, so every /member/* and /portal/* page gets a uniform
  // accent instead of inheriting whichever staff-facing module's keyword its
  // sub-path happens to contain (e.g. /member/events matching Calendar's
  // "/events", /member/pledges matching Finance's "/pledges") (#6380).
  // Segment-aware rather than a bare path.includes("/member") specifically
  // so it can't also match unrelated paths like /people/membership-pathways.
  if (
    path === "/member" || path.startsWith("/member/") ||
    path === "/member-portal" || path.startsWith("/member-portal/") ||
    path === "/portal" || path.startsWith("/portal/")
  ) {
    return getModuleAccent("memberPortal");
  }

  // Calendar / Scheduling
  if (path.includes("/calendar") || path.includes("/events") || path.includes("/schedule")) {
    return getModuleAccent("calendar");
  }

  // Finance
  if (
    path.includes("/finance") ||
    path.includes("/banking") ||
    path.includes("/budgets") ||
    path.includes("/contributions") ||
    path.includes("/pledges") ||
    path.includes("/funds")
  ) {
    return getModuleAccent("finance");
  }

  // Schools
  if (path.includes("/schools")) {
    return getModuleAccent("schools");
  }

  // People / Directory
  // Note: the People module's own member directory is /community/directory,
  // already covered by the "/community" check below — a separate bare
  // "/directory" clause was always redundant once scoped correctly, and it
  // also matched the unrelated /settings/directory admin page, giving it the
  // People accent instead of Settings' (#6378).
  if (
    path.includes("/people") ||
    path.includes("/profile") ||
    path.includes("/households") ||
    path.includes("/community")
  ) {
    return getModuleAccent("people");
  }

  // HR
  // Note: HR's own onboarding route is /hr/onboarding, already covered by the
  // "/hr" check above — a bare "/onboarding" clause here used to also catch
  // /volunteers/onboarding (checked lower down) since this branch runs first.
  if (
    path.includes("/hr") ||
    path.includes("/staff") ||
    path.includes("/offboarding") ||
    path.includes("/recruitment") ||
    path.includes("/performance") ||
    path.includes("/time-off")
  ) {
    return getModuleAccent("hr");
  }

  // Ministry
  if (path.includes("/ministry") || path.includes("/ministries")) {
    return getModuleAccent("ministry");
  }

  // Attendance / Check-in
  if (path.includes("/attendance") || path.includes("/check-in")) {
    return getModuleAccent("attendance");
  }

  // Volunteer
  if (path.includes("/volunteer") || path.includes("/volunteers")) {
    return getModuleAccent("volunteer");
  }

  // Messaging / Communications
  if (
    path.includes("/announcements") ||
    path.includes("/messaging") ||
    path.includes("/communications")
  ) {
    return getModuleAccent("messaging");
  }

  // Reports / Analytics
  if (path.includes("/reports") || path.includes("/analytics")) {
    return getModuleAccent("reports");
  }

  // Operations / Projects
  if (
    path.includes("/projects") ||
    path.includes("/tasks") ||
    path.includes("/automations") ||
    path.includes("/operations")
  ) {
    return getModuleAccent("projects");
  }

  // Settings
  if (path.includes("/settings") || path.includes("/integrations")) {
    return getModuleAccent("settings");
  }

  return getModuleAccent("home");
}
